<template>
  <v-card class="mb-4">
    <v-card-title class="d-flex justify-space-between align-center">
      <span>顧客ランキング</span>
      <div class="d-flex align-center">
        <v-select
          v-model="selectedType"
          :items="typeOptions"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          style="max-width: 120px;"
          class="mr-2"
          @change="handleTypeChange"
        />
        <v-select
          v-model="selectedLimit"
          :items="limitOptions"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          style="max-width: 100px;"
          @change="handleLimitChange"
        />
      </div>
    </v-card-title>

    <v-card-text>
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="mb-4"
      />

      <v-simple-table
        v-else-if="ranking.length > 0"
        dense
      >
        <template #default>
          <thead>
            <tr>
              <th
                class="text-center"
                style="width: 50px;"
              >
                順位
              </th>
              <th>顧客名</th>
              <th class="text-right">
                {{ selectedType === 'sales' ? '売上金額' : '来店回数' }}
              </th>
              <th class="text-right">
                {{ selectedType === 'sales' ? '来店回数' : '売上金額' }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in ranking"
              :key="item.customerId"
              class="cursor-pointer"
              @click="handleRowClick(item)"
            >
              <td class="text-center">
                <v-chip
                  v-if="item.rank <= 3"
                  :color="getRankColor(item.rank)"
                  small
                  text-color="white"
                >
                  {{ item.rank }}
                </v-chip>
                <span v-else>{{ item.rank }}</span>
              </td>
              <td>{{ item.customerName }}</td>
              <td class="text-right">
                <template v-if="selectedType === 'sales'">
                  {{ formatCurrency(item.value) }}
                </template>
                <template v-else>
                  {{ item.value }}回
                </template>
              </td>
              <td class="text-right text-caption grey--text">
                <template v-if="selectedType === 'sales'">
                  {{ item.visitCount }}回
                </template>
                <template v-else>
                  {{ formatCurrency(item.totalAmount) }}
                </template>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <div
        v-else
        class="text-center py-8 grey--text"
      >
        データがありません
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  ref, watch, computed,
} from '@vue/composition-api'
import { formatCurrency } from '@/utils/formatters'
import useCustomerRanking, { RANKING_TYPES, LIMIT_OPTIONS } from '../composables/useCustomerRanking'

export default {
  name: 'CustomerRanking',
  props: {
    startDate: {
      type: [String, Date],
      default: null,
    },
    endDate: {
      type: [String, Date],
      default: null,
    },
  },
  setup(props, { root, emit }) {
    const {
      ranking,
      loading,
      fetchRanking,
    } = useCustomerRanking(root)

    const selectedType = ref('sales')
    const selectedLimit = ref(10)

    const isLoading = computed(() => loading.value)

    const typeOptions = RANKING_TYPES
    const limitOptions = LIMIT_OPTIONS

    const getRankColor = rank => {
      if (rank === 1) return 'amber darken-2'
      if (rank === 2) return 'blue-grey lighten-1'
      if (rank === 3) return 'brown lighten-1'

      return 'grey'
    }

    const loadRanking = () => {
      fetchRanking({
        rankingType: selectedType.value,
        rankingLimit: selectedLimit.value,
        startDate: props.startDate,
        endDate: props.endDate,
      })
    }

    const handleTypeChange = () => {
      loadRanking()
    }

    const handleLimitChange = () => {
      loadRanking()
    }

    const handleRowClick = item => {
      emit('customer-click', item.customerId)
    }

    // 日付が変更されたらリロード
    watch(
      () => [props.startDate, props.endDate],
      () => {
        loadRanking()
      },
      { immediate: true },
    )

    return {
      ranking,
      isLoading,
      selectedType,
      selectedLimit,
      typeOptions,
      limitOptions,
      formatCurrency,
      getRankColor,
      handleTypeChange,
      handleLimitChange,
      handleRowClick,
    }
  },
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.cursor-pointer:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
